import { useMemo } from 'react';
import { captureExceptionEvent } from '@blocs.observability';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '../Heap';

export type SignupAddressValidationEvents = {
  trackAddressValidationResponse: (data: { verdict: string }) => void;
  trackAddressValidationModalShown: (data: { verdict: string }) => void;
  trackAddressValidationSuggestionClicked: () => void;
  trackAddressValidationNextClicked: () => void;
  trackAddressValidationExitClicked: () => void;
  trackAddressValidationCheckoutCompleted: () => void;
};

const track = (name: HEAP_EVENT_NAMES, data?: Record<string, unknown>) => {
  try {
    captureHeapEvent({ name, data });
  } catch (e) {
    captureExceptionEvent(e, { section: `Talent: heap "${name}" event` });
  }
};

// Google Address Validation is only tracked on the main signup Paywall, per product decision — the
// upgrade modal and the wallet's saved-card editor use the same validator, but double-tracking isn't
// needed, so there's no `source` param here and no upgrade-prefixed counterpart to these events. Kept
// as its own hook (rather than folding into usePaywallFunnelEvents) so a consumer that shouldn't track
// address validation (the upgrade flow, the standalone wallet-settings page) simply never calls this,
// with no dead/no-op tracker sitting on an object that implies it supports both flows.
export const useSignupAddressValidationEvents = (): SignupAddressValidationEvents =>
  useMemo<SignupAddressValidationEvents>(
    () => ({
      trackAddressValidationResponse: (data) => track(HEAP_EVENT_NAMES.SIGNUP_ADDRESS_VALIDATION_RESPONSE, data),
      trackAddressValidationModalShown: (data) => track(HEAP_EVENT_NAMES.SIGNUP_ADDRESS_VALIDATION_MODAL_SHOWN, data),
      trackAddressValidationSuggestionClicked: () =>
        track(HEAP_EVENT_NAMES.SIGNUP_ADDRESS_VALIDATION_SUGGESTION_CLICKED),
      trackAddressValidationNextClicked: () => track(HEAP_EVENT_NAMES.SIGNUP_ADDRESS_VALIDATION_NEXT_CLICKED),
      trackAddressValidationExitClicked: () => track(HEAP_EVENT_NAMES.SIGNUP_ADDRESS_VALIDATION_EXIT_CLICKED),
      trackAddressValidationCheckoutCompleted: () =>
        track(HEAP_EVENT_NAMES.SIGNUP_ADDRESS_VALIDATION_CHECKOUT_COMPLETED),
    }),
    []
  );
